import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  Grid,
  DialogActions,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API } from '../../config/config';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import { useAddressList } from '../../hooks/useAddressList';

// Define the AddressRequest interface based on the schema
interface AddressRequest {
  line1: string;
  line2?: string;
  city?: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

// Define the AdminAddressRequest interface based on the schema
interface AdminAddressRequest {
  email: string;
  address: AddressRequest;
}

// Define the Address interface with id for displaying existing addresses
interface Address {
  id: string;
  line1: string;
  line2?: string;
  city?: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

interface AddressFormProps {
  open: boolean;
  onClose: () => void;
  onAddressAdded: () => void;
  buyerEmail?: string;
}

const AddressForm: React.FC<AddressFormProps> = ({
  open,
  onClose,
  onAddressAdded,
  buyerEmail = ''
}) => {
  const { enhancedFetch } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formFields, setFormFields] = useState<AdminAddressRequest>({
    email: buyerEmail,
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      phoneNumber: ''
    }
  });
  const { createAddress } = useAddressList();

  const [errors, setErrors] = useState<Record<string, string>>({});


  // Handle input changes for text fields
  const handleInputChange = (field: string, isAddressField: boolean = false) =>
    useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      if (isAddressField) {
        setFormFields(prev => ({
          ...prev,
          address: {
            ...prev.address,
            [field]: newValue
          }
        }));
      } else {
        setFormFields(prev => ({
          ...prev,
          [field]: newValue
        }));
      }

      // Clear error for this field when user types
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }, [field, isAddressField, errors]);

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validate email
    if (!formFields.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formFields.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Validate required address fields
    if (!formFields.address.line1) {
      newErrors.line1 = 'Address line 1 is required';
    }
    
    if (!formFields.address.state) {
      newErrors.state = 'State is required';
    }
    
    if (!formFields.address.postalCode) {
      newErrors.postalCode = 'Postal code is required';
    }
    
    if (!formFields.address.country) {
      newErrors.country = 'Country is required';
    }
    
    if (!formFields.address.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10,15}$/.test(formFields.address.phoneNumber.replace(/\D/g, ''))) {
      newErrors.phoneNumber = 'Phone number is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please correct the errors in the form');
      return;
    }

    setIsLoading(true);
    try {
      await createAddress(formFields);

      // Reset form
      setFormFields({
        email: buyerEmail,
        address: {
          line1: '',
          line2: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          phoneNumber: ''
        }
      });

      // Notify parent component
      onAddressAdded();
      
      // Close the dialog
      onClose();
    } catch (error) {
      console.error('Error adding address:', error);
      toast.error('Failed to add address. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      onClose={onClose}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '8px',
          padding: '16px'
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        Add New Address
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Box
          component="form"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mt: 1
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Buyer Information
          </Typography>
          
          <TextField
            fullWidth
            label="Email"
            value={formFields.email}
            onChange={handleInputChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            disabled={!!buyerEmail}
          />

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
            Address Information
          </Typography>
          
          <Grid container gap={1} justifyContent={'space-between'}>
            <Grid item xs={12} >
              <TextField
                fullWidth
                label="Address Line 1"
                value={formFields.address.line1}
                onChange={handleInputChange('line1', true)}
                error={!!errors.line1}
                helperText={errors.line1}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2 (Optional)"
                value={formFields.address.line2}
                onChange={handleInputChange('line2', true)}
              />
            </Grid>
            
            <Grid item xs={12} md={5.8}>
              <TextField
                fullWidth
                label="City (Optional)"
                value={formFields.address.city}
                onChange={handleInputChange('city', true)}
              />
            </Grid>
            
            <Grid item xs={12} md={5.8}>
              <TextField
                fullWidth
                label="State"
                value={formFields.address.state}
                onChange={handleInputChange('state', true)}
                error={!!errors.state}
                helperText={errors.state}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={5.8}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formFields.address.postalCode}
                onChange={handleInputChange('postalCode', true)}
                error={!!errors.postalCode}
                helperText={errors.postalCode}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={5.8} >
              <TextField
                fullWidth
                label="Country"
                value={formFields.address.country}
                onChange={handleInputChange('country', true)}
                error={!!errors.country}
                helperText={errors.country}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formFields.address.phoneNumber}
                onChange={handleInputChange('phoneNumber', true)}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                required
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Address'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressForm;